import { useEffect, useState } from "react";
import type { Rig } from "./bookingApi";

const apiBase =
  window.location.hostname === "localhost" ? "" : "https://api.earthboundmartian.com";

export const WHEELBASE_THEME = {
  primaryColor: "#D32A46",
  secondaryColor: "#172B2D",
  textColor: "#172B2D",
  textSecondaryColor: "#172B2D",
  surfaceColor: "#f6e7ce",
  backgroundColor: "#f6e7ce",
  googleAnalyticsId: "G-0NT38WY8F9",
} as const;

export const WHEELBASE_THEME_ATTRS = {
  "primary-color": WHEELBASE_THEME.primaryColor,
  "secondary-color": WHEELBASE_THEME.secondaryColor,
  "text-color": WHEELBASE_THEME.textColor,
  "text-secondary-color": WHEELBASE_THEME.textSecondaryColor,
  "surface-color": WHEELBASE_THEME.surfaceColor,
  "background-color": WHEELBASE_THEME.backgroundColor,
  "google-analytics-id": WHEELBASE_THEME.googleAnalyticsId,
  "no-default-font": true,
} as const;

export interface WheelbaseConfig {
  dealerId: string;
  rentals: Partial<Record<Rig, string>>;
}

const FALLBACK: WheelbaseConfig = {
  dealerId: "4651527",
  rentals: { commodore: "471182" },
};

let pending: Promise<WheelbaseConfig> | undefined;

export async function fetchWheelbaseConfig(): Promise<WheelbaseConfig> {
  if (!pending) {
    pending = fetch(`${apiBase}/v1/wheelbase`)
      .then(async (response) => {
        if (!response.ok) {
          return FALLBACK;
        }
        const body = (await response.json()) as Partial<WheelbaseConfig>;
        const dealerId = String(body.dealerId || FALLBACK.dealerId);
        const rentals: WheelbaseConfig["rentals"] = { ...FALLBACK.rentals };
        if (body.rentals && typeof body.rentals === "object") {
          for (const [rig, id] of Object.entries(body.rentals)) {
            if (id) {
              rentals[rig as Rig] = String(id);
            }
          }
        }
        return { dealerId, rentals };
      })
      .catch(() => FALLBACK);
  }
  return pending;
}

export function useWheelbaseConfig(): WheelbaseConfig | null {
  const [config, setConfig] = useState<WheelbaseConfig | null>(null);
  useEffect(() => {
    let cancelled = false;
    void fetchWheelbaseConfig().then((next) => {
      if (!cancelled) {
        setConfig(next);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return config;
}
